import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Oval } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faUser, faPhone, faEye, faEyeSlash, faLock, faUserPlus } from "@fortawesome/free-solid-svg-icons"; // Added relevant icons

const Registration = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    termsAndCondition: false,
  });

  const [errors, setErrors] = useState({
    password: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });

    if (name === "password") {
      validatePassword(value);
      if (formData.confirmPassword) {
        // Re-validate confirm password if password changes
        validateConfirmPassword(formData.confirmPassword, value);
      }
    } else if (name === "confirmPassword") {
      validateConfirmPassword(value, formData.password);
    }
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const maxLength = 64;
    const regexNumber = /\d/; // At least one number
    const regexLowerCase = /[a-z]/; // At least one lowercase
    const regexUpperCase = /[A-Z]/; // At least one uppercase
    const regexSpecialChar = /[@#$%^&*(),.?":{}|<>]/; // At least one special character

    let errorMessage = "";

    if (password.length < minLength || password.length > maxLength) {
      errorMessage = `Password must be between ${minLength}-${maxLength} characters.`;
    } else if (!regexNumber.test(password)) {
      errorMessage = "Password must contain at least one number.";
    } else if (!regexLowerCase.test(password)) {
      errorMessage = "Password must contain at least one lowercase letter.";
    } else if (!regexUpperCase.test(password)) {
      errorMessage = "Password must contain at least one uppercase letter.";
    } else if (!regexSpecialChar.test(password)) {
      errorMessage = "Password must contain at least one special character.";
    }

    setErrors((prev) => ({ ...prev, password: errorMessage }));
    return errorMessage === ""; // Return true if valid
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    const errorMessage =
      confirmPassword !== password ? "Passwords do not match." : "";
    setErrors((prev) => ({ ...prev, confirmPassword: errorMessage }));
    return errorMessage === ""; // Return true if valid
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const toggleConfirmPasswordVisibility = () => {
    setConfirmPasswordVisible(!confirmPasswordVisible);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Re-validate all fields on submit
    const isPasswordValid = validatePassword(formData.password);
    const isConfirmPasswordValid = validateConfirmPassword(formData.confirmPassword, formData.password);

    if (!isPasswordValid || !isConfirmPasswordValid || !formData.termsAndCondition) {
      toast.error("Please correct the errors and agree to terms.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        "https://backend.inyumba.co.ke/iNyumba/auth/register",
        formData,
        {
          params: {
            accountType: "CUSTOMER",
          },
        }
      );
      toast.success(
        "Registration successful! Please check your email to complete verification."
      );
      navigate("/");
    } catch (error) {
      console.error("Registration error:", error);
      if (error.response) {
        if (error.response.status === 401) {
          toast.error("Unauthorized access.");
        } else if (error.response.status === 406) {
          toast.error(
            "Wrong password format. Password must contain a number, capital letter, small letter, and special character (e.g., P@ssw0rd10{})."
          );
        } else if (error.response.data && error.response.data.response) {
          toast.error(error.response.data.response); // Display specific backend error message
        } else {
          toast.error("An error occurred during registration. Please try again later.");
        }
      } else {
        toast.error("Network error or server unreachable. Please try again later.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100 p-4 sm:p-6 lg:p-8">
      <div className="max-w-xl w-full bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300 p-0 overflow-hidden animate-fade-in-up">
        {/* Header Section with Gradient */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-700 p-8 text-white text-center">
          {/* Replace with your actual logo */}
          {/* <img className="mx-auto h-20 w-auto mb-4" src="/your-logo.svg" alt="iNyumba Logo" /> */}
          <FontAwesomeIcon icon={faUserPlus} className="mx-auto text-white text-6xl mb-4 opacity-90" />
          <h2 className="text-5xl font-extrabold mb-2">
            Create Your Account
          </h2>
          <p className="text-lg opacity-90">
            Join iNyumba today!
          </p>
        </div>

        {/* Form Section */}
        <div className="p-8 space-y-6">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Email Input */}
            <div>
              <label htmlFor="email" className="sr-only">Email</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faEnvelope} className="text-gray-500 text-lg" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="block w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-400 focus:border-indigo-400 sm:text-lg transition-all duration-200"
                  placeholder="Email address"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Name Input */}
            <div>
              <label htmlFor="name" className="sr-only">Name</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faUser} className="text-gray-500 text-lg" />
                </div>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  className="block w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-400 focus:border-indigo-400 sm:text-lg transition-all duration-200"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Phone Number Input */}
            <div>
              <label htmlFor="phoneNumber" className="sr-only">Phone Number</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faPhone} className="text-gray-500 text-lg" />
                </div>
                <input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  autoComplete="tel"
                  required
                  className="block w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-400 focus:border-indigo-400 sm:text-lg transition-all duration-200"
                  placeholder="Phone Number (e.g., +2547XXXXXXXX)"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label htmlFor="password" className="sr-only">Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faLock} className="text-gray-500 text-lg" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={passwordVisible ? "text" : "password"}
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="block w-full pl-12 pr-12 py-3 border-2 border-gray-200 rounded-xl placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-400 focus:border-indigo-400 sm:text-lg transition-all duration-200"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 hover:text-indigo-600 focus:outline-none transition-colors duration-200"
                  title={passwordVisible ? "Hide password" : "Show password"}
                >
                  <FontAwesomeIcon icon={passwordVisible ? faEyeSlash : faEye} className="text-lg" />
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
              )}
            </div>

            {/* Confirm Password Input */}
            <div>
              <label htmlFor="confirmPassword" className="sr-only">Confirm Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon={faLock} className="text-gray-500 text-lg" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={confirmPasswordVisible ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="block w-full pl-12 pr-12 py-3 border-2 border-gray-200 rounded-xl placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-400 focus:border-indigo-400 sm:text-lg transition-all duration-200"
                />
                <button
                  type="button"
                  onClick={toggleConfirmPasswordVisibility}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 hover:text-indigo-600 focus:outline-none transition-colors duration-200"
                  title={confirmPasswordVisible ? "Hide confirm password" : "Show confirm password"}
                >
                  <FontAwesomeIcon icon={confirmPasswordVisible ? faEyeSlash : faEye} className="text-lg" />
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Terms and Conditions Checkbox */}
            <div className="flex items-center justify-between">
              <label htmlFor="termsAndCondition" className="flex items-center text-base text-gray-600 cursor-pointer">
                <input
                  id="termsAndCondition"
                  name="termsAndCondition"
                  type="checkbox"
                  checked={formData.termsAndCondition}
                  onChange={handleChange}
                  className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded-md"
                />
                <span className="ml-2">
                  I agree to the{" "}
                  <a href="#" className="font-semibold text-indigo-600 hover:text-indigo-700 underline">
                    terms and conditions
                  </a>
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading || !formData.termsAndCondition || errors.password || errors.confirmPassword}
                className={`w-full py-3 px-6 rounded-xl text-xl font-bold transition-all duration-300 ease-in-out flex items-center justify-center space-x-3 shadow-lg
                  ${isLoading || !formData.termsAndCondition || errors.password || errors.confirmPassword
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-indigo-600 to-purple-700 text-white hover:from-indigo-700 hover:to-purple-800 transform hover:scale-105"
                  }`}
              >
                {isLoading ? (
                  <>
                    <Oval
                      height={24}
                      width={24}
                      color="white"
                      ariaLabel="loading-spinner"
                      secondaryColor="rgba(255,255,255,0.4)"
                      strokeWidth={4}
                      strokeWidthSecondary={2}
                    />
                    <span>Registering...</span>
                  </>
                ) : (
                  <span>Register</span>
                )}
              </button>
            </div>
          </form>

          {/* Login Link */}
          <div className="text-center text-base mt-8">
            <p className="text-gray-600">
              Already have an account?{" "}
              <a
                href="/"
                className="font-bold text-indigo-600 hover:text-indigo-700 transition-colors duration-200"
              >
                Sign In
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Registration;